import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Global site content (brand name, hero copy, phone, hours, advisory, etc.)
 * loaded once at app boot and provided to every page. Fields match the
 * backend `SiteSettings` model.
 */

const DEFAULT_SETTINGS = {
  brand_name: "Blossom's Kitchen",
  hero_welcome: "Welcome to",
  hero_tagline: "We are not fast food. We are goooooood food!",
  hero_cta_label: "Order Now",
  owner_line: "Proudly owned & operated by",
  owner_names: "Dillon & Jordan Suggs",
  phone: "479-903-5622",
  hours: "11a–2p · M–F",
  advisory:
    "Consuming raw or undercooked meats, poultry, seafood, shellfish, or eggs may increase your risk of foodborne illness, especially if you have certain medical conditions.",
  rewards_signup_headline: "Join Blossom's Rewards",
  rewards_signup_tagline: "Earn 1 point per $1 spent.",
  loyalty_card_headline: "Earn points on every order",
  loyalty_card_subtext:
    "Sign in with your phone — free drinks, wraps & burgers await",
  checkout_footer:
    "Your card is charged now. Order goes straight to the kitchen — pick it up at the truck.",
  sales_tax_rate: 0.105,
  orders_open: true,
  orders_closed_message:
    "You're a little early — please try during our hours of operation.",
  drinks_free_enabled: true,
  drinks_free_headline: "Drinks are FREE with every online order!",
  drinks_free_subtitle:
    "Pick yours at checkout · Thanks for choosing Blossom's Kitchen",
  drinks_free_window_enabled: false,
  drinks_free_starts_at: "",
  drinks_free_ends_at: "",
  // Authoritative "are drinks free right now?" computed by the backend.
  drinks_are_free_now: true,
  open_banner_video_url:
    "https://customer-assets-m6fa6gv7.emergentagent.net/job_mobile-food-app-4/artifacts/m5j4cqih_gemini_generated_video_7da0a5da.mp4",
  closed_banner_video_url:
    "https://customer-assets-m6fa6gv7.emergentagent.net/job_mobile-food-app-4/artifacts/kl58gnvj_gemini_generated_video_c13b16ae.mp4",
  about_headline: "Made with love, seasoned with heart.",
  about_story:
    "Blossom's Kitchen started as a family dream — a food truck built on comfort classics, big flavors, and even bigger smiles. Every burger, wrap, and side is made to order with fresh ingredients we'd feed our own family.",
  about_mission:
    "To bring people together over great food. We believe a warm meal, a kind word, and a fair price can turn a bad day around — and we serve every guest like they're family.",
  about_founded_year: "2024",
  featured_items: [],
  pos_grid_columns: 4,
  text_overrides: {},
  menu_categories: [
    "Burgers",
    "Sandwiches",
    "Loaded Fries",
    "Vegetarian",
    "Wraps",
    "Drinks",
    "Add-Ons",
  ],
  menu_category_colors: {},
};

const SiteSettingsContext = createContext({
  settings: DEFAULT_SETTINGS,
  refresh: () => {},
});

export function SiteSettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  const refresh = async () => {
    try {
      const { data } = await api.get("/site-settings");
      setSettings({ ...DEFAULT_SETTINGS, ...data });
    } catch (err) {
      // Fall back to defaults — nothing to render differently on error.
      console.debug("Site settings fetch failed:", err?.message || err);
    }
  };

  useEffect(() => {
    refresh();
    // Poll every 30s so the master orders-open toggle propagates to open
    // customer tabs without a full page reload.
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, []);

  return (
    <SiteSettingsContext.Provider value={{ settings, refresh }}>
      {children}
    </SiteSettingsContext.Provider>
  );
}

export function useSiteSettings() {
  return useContext(SiteSettingsContext);
}
